import type {
  InviteIntegrationSource,
  InvitePlatform,
  InviteUnavailableReason,
  InviteViewPhase,
} from "./inviteTypes";
import {
  isPrixiInvitePhase,
  isInviteConnectLifecyclePhase,
} from "./invitePhaseUtils";
import { productBrand } from "../../productBrand";

const PLATFORM_SUCCESS_TITLE: Record<string, string> = {
  youtube: "¡YouTube conectado!",
  tiktok: "¡TikTok conectado!",
  meta: "¡Instagram conectado!",
  spotify: "¡Spotify conectado!",
};

const PLATFORM_DISCONNECTED_TITLE: Record<string, string> = {
  youtube: "¡YouTube desconectado!",
  tiktok: "¡TikTok desconectado!",
  meta: "¡Instagram desconectado!",
  spotify: "¡Spotify desconectado!",
};

export function platformLabel(platform: InvitePlatform): string {
  switch (platform) {
    case "meta":
      return "Instagram";
    case "youtube":
      return "YouTube";
    case "tiktok":
      return "TikTok";
    case "spotify":
      return "Spotify";
  }
}

export function getInviteTitle(
  phase: InviteViewPhase,
  opts: {
    successParam: string | null;
    disconnectedParam: string | null;
    unavailableReason?: InviteUnavailableReason | null;
    platform?: InvitePlatform;
  },
): string {
  switch (phase) {
    case "loading":
      return "Cargando invitación…";
    case "revalidating":
      return "Verificando conexión…";
    case "fatal":
      if (opts.unavailableReason === "not_found") {
        return "Enlace no encontrado";
      }
      return "No pudimos cargar la invitación";
    case "expired":
    case "prixi_expired":
      return "Esta invitación expiró";
    case "unavailable":
    case "prixi_unavailable":
      if (opts.unavailableReason === "already_used") {
        return "Este enlace ya fue utilizado";
      }
      return "Este enlace ya no está disponible";
    case "connect":
    case "connect_error":
    case "meta_page_id":
    case "meta_podcast_select":
    case "meta_mapping":
      return "¡Recibiste una invitación!";
    case "prixi_connect":
    case "prixi_connect_error":
    case "prixi_meta_page_id":
    case "prixi_meta_podcast_select":
    case "prixi_meta_mapping":
      return `Conecta tu cuenta de ${platformLabel(opts.platform ?? "youtube")}`;
    case "connecting":
    case "prixi_connecting":
      return "Conectando cuenta…";
    case "success":
    case "prixi_success":
      return (
        PLATFORM_SUCCESS_TITLE[opts.successParam ?? ""] ?? "¡Cuenta conectada!"
      );
    case "already_connected":
    case "prixi_already_connected":
      return "Cuenta conectada";
    case "disconnected":
    case "prixi_disconnected":
      return (
        PLATFORM_DISCONNECTED_TITLE[opts.disconnectedParam ?? ""] ??
        "Cuenta desconectada"
      );
    default:
      return "¡Recibiste una invitación!";
  }
}

export function getInviteSubtitle(
  phase: InviteViewPhase,
  opts: {
    createdBy: string;
    podcastTitle?: string;
    companyName?: string;
    fatalError?: string | null;
    unavailableReason?: InviteUnavailableReason | null;
    platform?: InvitePlatform;
  },
): string | null {
  const contactName = opts.companyName || opts.createdBy;

  switch (phase) {
    case "fatal":
      return (
        opts.fatalError ??
        "Ocurrió un problema al cargar la invitación. Intenta de nuevo en unos minutos."
      );
    case "expired":
      return contactName
        ? `El plazo para usar este enlace terminó. Pídele a ${contactName} que te envíe una invitación nueva desde Podscope.`
        : "El plazo para usar este enlace terminó. Pídele al equipo del podcast que te envíe una invitación nueva.";
    case "prixi_expired":
      return "El plazo para usar este enlace terminó. Genera un enlace nuevo desde Prixi → Cuentas conectadas.";
    case "unavailable":
      if (opts.unavailableReason === "already_used") {
        return contactName
          ? `La cuenta ya fue conectada con este enlace. Si necesitas cambiarla, pídele a ${contactName} un enlace nuevo desde la configuración del podcast.`
          : "La cuenta ya fue conectada con este enlace. Si necesitas cambiarla, solicita un enlace nuevo.";
      }
      return contactName
        ? `${contactName} puede haber generado un enlace más reciente. Pídele que te comparta la invitación activa desde Podscope.`
        : "Es posible que exista un enlace más reciente. Solicita una nueva invitación al equipo del podcast.";
    case "prixi_unavailable":
      if (opts.unavailableReason === "already_used") {
        return "La cuenta ya fue conectada con este enlace. Si necesitas cambiarla, genera un enlace nuevo desde Prixi.";
      }
      return "Es posible que exista un enlace más reciente. Genera uno nuevo desde Prixi → Cuentas conectadas.";
    case "connect":
    case "connect_error":
    case "meta_page_id":
    case "meta_podcast_select":
    case "meta_mapping":
      return formatInviterCopy(opts.createdBy, opts.podcastTitle);
    case "connecting":
      return null;
    case "prixi_connect":
    case "prixi_connect_error":
    case "prixi_meta_page_id":
    case "prixi_meta_podcast_select":
    case "prixi_meta_mapping":
    case "prixi_connecting":
      return formatPrixiConnectCopy(opts.podcastTitle);
    case "success":
      return "La cuenta fue conectada correctamente.";
    case "prixi_success":
      return "Listo. Vuelve a la pestaña de Prixi.";
    case "already_connected":
      return "Ya existe una cuenta vinculada a esta invitación.";
    case "prixi_already_connected":
      return "Cierra esta pestaña y continúa en Prixi.";
    case "disconnected":
      return "La cuenta fue desconectada y todos los datos fueron eliminados de Podscope.";
    case "prixi_disconnected":
      return "La cuenta fue desconectada. Vuelve a Prixi para generar un nuevo enlace de conexión.";
    case "revalidating":
      return "Un momento, estamos confirmando el estado de tu cuenta.";
    case "loading":
      return null;
    default:
      return null;
  }
}

function formatInviterCopy(createdBy: string, podcastTitle?: string): string {
  if (podcastTitle) {
    return `${createdBy} te ha invitado al podcast “${podcastTitle}”.`;
  }
  return `${createdBy} te ha invitado a conectar tu cuenta.`;
}

function formatPrixiConnectCopy(podcastTitle?: string): string {
  const partnershipNote =
    "Podscope es el partner de datos de Prixi; no necesitas registrarte aquí.";
  if (podcastTitle) {
    return `Para sincronizar métricas de “${podcastTitle}” en Prixi.\n${partnershipNote}`;
  }
  return `Para sincronizar las métricas de tu canal en Prixi.\n${partnershipNote}`;
}

export function getInviteDocumentTitle(
  viewPhase: InviteViewPhase,
  opts: {
    platform?: InvitePlatform;
    integrationSource?: InviteIntegrationSource;
  } = {},
): string {
  const platform = opts.platform ?? "youtube";
  const isPrixi =
    opts.integrationSource === "prixi" || isPrixiInvitePhase(viewPhase);

  if (!isPrixi) {
    return `Invitación · ${productBrand.name}`;
  }

  const platformName = platformLabel(platform);

  if (isInviteConnectLifecyclePhase(viewPhase)) {
    return `Conectar ${platformName} · Prixi`;
  }
  if (viewPhase === "prixi_success") {
    return `${platformName} conectado · Prixi`;
  }
  if (viewPhase === "prixi_already_connected") {
    return "Cuenta conectada · Prixi";
  }
  if (viewPhase === "prixi_disconnected") {
    return "Cuenta desconectada · Prixi";
  }
  return "Conexión · Prixi";
}

export function getStaticInstructions(platform: InvitePlatform): string[] {
  switch (platform) {
    case "spotify":
      return [
        "Acepta nuestros términos y condiciones",
        "Copia la URL de tu podcast en Spotify",
        "Pégala en el campo de abajo",
        'Haz clic en "Conectar Spotify"',
      ];
    case "youtube":
      return [
        "Acepta nuestros términos y condiciones",
        'Haz clic en "Conectar YouTube"',
        "Inicia sesión con Google",
        "Acepta los permisos solicitados de solo lectura",
      ];
    case "tiktok":
      return [
        "Acepta nuestros términos y condiciones",
        'Haz clic en "Conectar TikTok"',
        "Inicia sesión con TikTok",
        "Acepta los permisos solicitados de solo lectura",
      ];
    case "meta":
      return [
        "Acepta nuestros términos y condiciones",
        'Haz clic en "Conectar Instagram"',
        "Autoriza el acceso con Facebook",
        "Selecciona tu cuenta de Instagram y acepta los permisos",
      ];
  }
}

export function getMetaPageIdInstructions(): string[] {
  return [
    "Acepta nuestros términos y condiciones",
    "Ingresa el Facebook Page ID asociado a tu Instagram",
    'Presiona "Conectar Instagram con Page ID"',
  ];
}

export function getMetaConnectInstructions(): string[] {
  return [
    "Acepta nuestros términos y condiciones",
    "Selecciona los podcasts a conectar",
    'Presiona "Continuar con Facebook"',
    "Selecciona tu cuenta de Instagram y acepta los permisos",
  ];
}

export function getMetaMappingInstructions(): string[] {
  return [
    "Acepta nuestros términos y condiciones",
    "Asocia cada cuenta de Instagram con su podcast",
    'Presiona "Confirmar vinculación"',
  ];
}
